import { useState, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/auth-context";
import { DiscoveryContext } from "../discovery-context";
import { Checkbox, FormControlLabel, TextField, IconButton, Chip, Radio, RadioGroup } from "@mui/material";
import { Add as AddIcon, Close as CloseIcon, AddCircleOutline, InfoOutlined } from "@mui/icons-material";
import Modal from "../../../Component/Modal/Modal";
import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import MUIMultiSelect from "../../../Component/UI/MUIMultiSelect/MUIMultiSelect";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import classes from "./Setting.module.css";
import { resolveDefaultKeywordOptions, resolveMaxKeywords } from "../../../utils/keywordOptions";
import { isCompositeIndexScoreType } from "../../../utils/brandIndexCalc";
import { hasBatchCapableLlm } from "../../../utils/batchCapableLlms";

const MAX_RANK_COMPETITIVE_SET = 10;

export default function DiscoverySettingsModal({
    open,
    onClose,
    metricSettingId,
    onMetricSettingChange,
    keywords,
    setKeywords,
    customIndexScoreType,
    onCustomIndexScoreTypeChange = () => { },
    viewMode = false,
    keyPlayers = [],
    keyPlayersLoading = false,
    rankCompetitiveSet = [],
    onRankCompetitiveSetChange = () => { },
    rankMethodology = "by_category",
    onRankMethodologyChange = () => { },
    mainBrand = "",
    formCompetitors = [],
    isBatchMode = false,
    onBatchModeChange = () => { },
    isNonBrandedQuestions = false,
    onNonBrandedQuestionsChange = () => { },
    isFactBasedQuestions = false,
    onFactBasedQuestionsChange = () => { },
    llm = "",
}) {
    const dispatch = useDispatch();
    const { user, customerUiInfo } = useContext(AuthContext);
    const discoveryCtx = useContext(DiscoveryContext);
    const userMetricSetting = useSelector((store) => store.userSet);

    const DEFAULT_KEYWORD_OPTIONS = resolveDefaultKeywordOptions(customerUiInfo);

    const { customIndexScoreTypes } = customerUiInfo || {};
    const hasCustomIndexScoreTypes =
        Array.isArray(customIndexScoreTypes) && customIndexScoreTypes.length > 0;
    // No manual picker here — Form.js already defaults customIndexScoreType
    // to the customer's type whenever they have one configured.
    const isCustomIndexScoreTypeSelected = hasCustomIndexScoreTypes && !!customIndexScoreType;

    const baseTemplateOptions = (userMetricSetting?.data ?? [])
        .filter((ele) => {
            if (!hasCustomIndexScoreTypes) return true;
            return customIndexScoreType
                ? ele.custom_index_score_type === customIndexScoreType
                : !ele.custom_index_score_type;
        })
        .map((ele) => ({
            name: ele.name,
            value: ele.id,
            isDefault: ele.isDefault,
        }));

    // In view mode, merge entries from user_metric_setting_object so the selected
    // ID resolves to a name even when it's not in the standard template list
    const metricObj = discoveryCtx?.metricSettingObject ?? {};
    const knownValues = new Set(baseTemplateOptions.map((o) => String(o.value)));
    const extraOptions = Object.values(metricObj)
        .filter((entry) => entry?.id != null && !knownValues.has(String(entry.id)))
        .map((entry) => ({
            name: entry.name,
            value: entry.id === "default" ? "default" : Number(entry.id),
            isDefault: entry.isDefault ?? false,
        }));
    const templateOptions = viewMode ? [...baseTemplateOptions, ...extraOptions] : baseTemplateOptions;

    const selectedTemplateData =
        (userMetricSetting?.data ?? []).find((ele) => ele.id == metricSettingId) ?? {};

    const handleNonBrandedQuestionsChange = (checked) => {
        if (checked && !(selectedTemplateData?.["non-branded-questions"]?.length > 0)) {
            toast.error("The selected template has no Open Ended Questions configured. Add some in Template Settings first.");
            return;
        }
        onNonBrandedQuestionsChange(checked);
    };

    const handleFactBasedQuestionsChange = (checked) => {
        if (checked && !(selectedTemplateData?.["fact-based-questions"]?.length > 0)) {
            toast.error("The selected template has no Fact Based Questions configured. Add some in Template Settings first.");
            return;
        }
        onFactBasedQuestionsChange(checked);
    };
    const templateFactorOptions = Array.from(
        new Set(
            (selectedTemplateData?.visibility ?? [])
                .map((v) => v.factor)
                .filter(Boolean)
        )
    );
    const KEYWORD_OPTIONS =
        isCustomIndexScoreTypeSelected && templateFactorOptions.length > 0
            ? templateFactorOptions
            : DEFAULT_KEYWORD_OPTIONS;
    const MAX_KEYWORDS = resolveMaxKeywords(user?.email, KEYWORD_OPTIONS.length);
    const keywordOptionsKey = KEYWORD_OPTIONS.join("|");


    useEffect(() => {
        if (viewMode) return;
        setKeywords((prev) => {
            const filtered = prev.filter((k) => KEYWORD_OPTIONS.includes(k));
            if (filtered.length === prev.length) return prev;
            // never leave zero selected — fall back to selecting everything available
            return filtered.length > 0 ? filtered : [...KEYWORD_OPTIONS];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywordOptionsKey]);

    useEffect(() => {
        if (viewMode || !hasCustomIndexScoreTypes) return;
        const currentValue = metricSettingId === "default" ? "default" : Number(metricSettingId);
        const stillValid = templateOptions.some((opt) => opt.value === currentValue);
        if (!stillValid) {
            const preferredTemplate = templateOptions.find((opt) => opt.isDefault) ?? templateOptions[0];
            onMetricSettingChange(preferredTemplate?.value ?? "default");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customIndexScoreType, userMetricSetting?.data]);

    const [customKeywordInput, setCustomKeywordInput] = useState("");

    const allKeywordOptions = [
        ...KEYWORD_OPTIONS,
        ...keywords.filter((k) => !KEYWORD_OPTIONS.includes(k)),
    ];

    const handleKeywordToggle = (keyword, checked) => {
        if (viewMode) return;
        if (checked) {
            if (keywords.length >= MAX_KEYWORDS) return;
            setKeywords((prev) => [...prev, keyword]);
        } else {
            if (keywords.length <= 1) {
                toast.warn("At least one Visibility Attribute is required.");
                return;
            }
            setKeywords((prev) => prev.filter((k) => k !== keyword));
        }
    };

    const handleAddCustomKeyword = () => {
        if (viewMode) return;
        const trimmed = customKeywordInput.trim();
        if (!trimmed) return;
        if (allKeywordOptions.some((k) => k.toLowerCase() === trimmed.toLowerCase())) return;
        if (keywords.length >= MAX_KEYWORDS) {
            setKeywords((prev) => [...prev.slice(0, MAX_KEYWORDS - 1), trimmed]);
        } else {
            setKeywords((prev) => [...prev, trimmed]);
        }
        setCustomKeywordInput("");
    };

    const handleRemoveCustomKeyword = (keyword) => {
        if (viewMode) return;
        if (keywords.length <= 1) {
            toast.warn("At least one Visibility Attribute is required.");
            return;
        }
        setKeywords((prev) => prev.filter((k) => k !== keyword));
    };

    const isComposite = isCompositeIndexScoreType(customIndexScoreType);
    const showRankCompetitiveSet = isComposite || !!user?.is_custom_rank;

    const lockedRankNames = Array.from(
        new Set([mainBrand, ...(formCompetitors || [])].filter(Boolean))
    );

    const additionalRankSelected = (rankCompetitiveSet || []).filter(
        (name) => !lockedRankNames.includes(name)
    );
    const maxAdditionalRank = Math.max(0, MAX_RANK_COMPETITIVE_SET - lockedRankNames.length);

    const rankCompetitiveSetOptions = (() => {
        const apiPlayers = (keyPlayers || [])
            .map((player) => ({ name: player.name, value: player.name }))
            .filter((p) => !lockedRankNames.includes(p.value));
        const existingSelected = additionalRankSelected
            .filter((name) => !apiPlayers.some((p) => p.value === name))
            .map((name) => ({ name, value: name }));
        return [...existingSelected, ...apiPlayers];
    })();

    const handleRankCompetitiveSetChange = (selected) => {
        if (viewMode) return;
        const cleaned = selected.filter((n) => !lockedRankNames.includes(n));
        if (cleaned.length > maxAdditionalRank) {
            toast.warn(`You can select up to ${MAX_RANK_COMPETITIVE_SET} competitors in total.`);
            onRankCompetitiveSetChange(cleaned.slice(0, maxAdditionalRank));
            return;
        }
        onRankCompetitiveSetChange(cleaned);
    };

    function CustomOption(option) {
        const { name, isDefault } = option;
        return (
            <span className={isDefault ? classes.isDefault : classes.isNotDefault}>
                {name}{isDefault ? " ★" : ""}
            </span>
        );
    }
    return (
        <Modal
            handleClose={onClose}
            open={open}
            title="Configuration Settings"
            width="750"
            showActions={false}
        >
            <div className={classes.modalContent}>
                <div className={classes.modalBody}>
                    {!!user?.is_batch_mode_enabled && hasBatchCapableLlm(llm) && (
                        <div className={classes.settingSection}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isBatchMode}
                                        onChange={(e) => onBatchModeChange(e.target.checked)}
                                        disabled={viewMode}
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": { color: "#2dd4bf" },
                                            padding: "4px",
                                        }}
                                    />
                                }
                                label="Batch Mode"
                                sx={{
                                    ".MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </div>
                    )}

                    {!!customerUiInfo?.is_non_branded_questions && (
                        <div className={classes.settingSection} style={{ display: "flex", flexDirection: "row", gap: "1.5rem" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isNonBrandedQuestions}
                                        onChange={(e) => handleNonBrandedQuestionsChange(e.target.checked)}
                                        disabled={viewMode}
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": { color: "#2dd4bf" },
                                            padding: "4px",
                                        }}
                                    />
                                }
                                label="Open Ended Questions"
                                sx={{
                                    ".MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                    },
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isFactBasedQuestions}
                                        onChange={(e) => handleFactBasedQuestionsChange(e.target.checked)}
                                        disabled={viewMode}
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": { color: "#2dd4bf" },
                                            padding: "4px",
                                        }}
                                    />
                                }
                                label="Fact Based Questions"
                                sx={{
                                    ".MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </div>
                    )}

                    {/* Template Settings Section */}
                    <div className={classes.settingSection}>
                        <div className={classes.sectionHeader}>
                            <div>
                                <h4 className={classes.sectionTitle}>
                                    Global Template Settings
                                    <InfoOutlined
                                        className={classes.infoIcon}
                                        titleAccess="This template applies to all categories."
                                    />
                                </h4>
                                <p className={classes.sectionSubtitle}>Default template for all categories.</p>
                            </div>
                        </div>
                        <div className={classes.globalSelectContainer}>
                            <div className={classes.selectWrapper}>
                                <MUISelect
                                    fullWidth={true}
                                    optionValue={
                                        metricSettingId === "default"
                                            ? "default"
                                            : Number(metricSettingId)
                                    }
                                    label=""
                                    options={templateOptions}
                                    disabled={viewMode}
                                    CustomOption={CustomOption}
                                    onChange={(e) => onMetricSettingChange(e.target.value)}
                                />
                            </div>
                            {!!user?.is_template_setting_enabled && !viewMode && (
                                <MUIIconButton
                                    className={classes.addButton}
                                    title="Manage Templates"
                                    onClick={() => {
                                        dispatch({ type: "ACTION_MODEL", payload: true });
                                    }}
                                >
                                    <AddCircleOutline />
                                </MUIIconButton>
                            )}
                        </div>
                    </div>

                    {/* Visibility Attributes Section */}
                    <div className={classes.settingSection}>
                        <h4 className={classes.settingSectionTitle}>
                            Visibility Attributes
                            <span className={classes.keywordCount}>
                                ({keywords.length}/{MAX_KEYWORDS} selected)
                            </span>
                        </h4>
                        <div className={classes.keywordsContainer}>
                            {KEYWORD_OPTIONS.map((keyword) => (
                                <FormControlLabel
                                    key={keyword}
                                    control={
                                        <Checkbox
                                            checked={keywords.includes(keyword)}
                                            onChange={(e) =>
                                                handleKeywordToggle(keyword, e.target.checked)
                                            }
                                            disabled={
                                                viewMode ||
                                                (!keywords.includes(keyword) && keywords.length >= MAX_KEYWORDS)
                                            }
                                            sx={{
                                                color: "#d1d5db",
                                                "&.Mui-checked": { color: "#2dd4bf" },
                                                padding: "4px",
                                            }}
                                        />
                                    }
                                    label={keyword}
                                    sx={{
                                        "&.MuiFormControlLabel-root": {
                                            padding: "0.5rem 1rem",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "0.75rem",
                                            margin: "0 !important",
                                            background: keywords.includes(keyword)
                                                ? "#f0fdfa"
                                                : "white",
                                        },
                                        ".MuiFormControlLabel-label": {
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                        },
                                    }}
                                />
                            ))}
                        </div>

                        {/* Custom keywords chips */}
                        {keywords.filter((k) => !KEYWORD_OPTIONS.includes(k)).length > 0 && (
                            <div className={classes.customKeywordsChips}>
                                {keywords
                                    .filter((k) => !KEYWORD_OPTIONS.includes(k))
                                    .map((keyword) => (
                                        <Chip
                                            key={keyword}
                                            label={keyword}
                                            onDelete={viewMode ? undefined : () => handleRemoveCustomKeyword(keyword)}
                                            deleteIcon={
                                                <CloseIcon style={{ fontSize: "16px" }} />
                                            }
                                            sx={{
                                                background: "#f0fdfa",
                                                border: "1px solid #2dd4bf",
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                            }}
                                        />
                                    ))}
                            </div>
                        )}
                        {!viewMode && !isCustomIndexScoreTypeSelected && (
                            <div className={classes.customKeywordInput}>
                                <TextField
                                    size="small"
                                    placeholder="Add custom Attribute..."
                                    value={customKeywordInput}
                                    onChange={(e) => setCustomKeywordInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleAddCustomKeyword();
                                        }
                                    }}
                                    sx={{ flex: 1 }}
                                />
                                <IconButton
                                    onClick={handleAddCustomKeyword}
                                    disabled={!customKeywordInput.trim()}
                                    sx={{
                                        border: "1px solid #d1d5db",
                                        borderRadius: "0.5rem",
                                        color: "#2dd4bf",
                                        "&:hover": { background: "#f0fdfa" },
                                    }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </div>
                        )}
                    </div>

                    {/* Rank Methodology Section — only when the user has this feature enabled */}
                    {!!user?.is_custom_rank && (
                        <div className={classes.settingSection}>
                            <h4 className={classes.settingSectionTitle}>Rank Methodology</h4>
                            <RadioGroup
                                value={rankMethodology}
                                onChange={(e) => onRankMethodologyChange(e.target.value)}
                            >
                                <FormControlLabel
                                    value="by_category"
                                    control={<Radio disabled={viewMode} sx={{ "&.Mui-checked": { color: "#2dd4bf" } }} />}
                                    label="By Category"
                                    sx={{ ".MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 } }}
                                />
                                <FormControlLabel
                                    value="by_named_competitor"
                                    control={<Radio disabled={viewMode} sx={{ "&.Mui-checked": { color: "#2dd4bf" } }} />}
                                    label="By Named Competitor"
                                    sx={{ ".MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 } }}
                                />
                            </RadioGroup>
                        </div>
                    )}

                    {/* Rank Competitive Set Section — for direct_and_composite or the By Named Competitor methodology */}
                    {showRankCompetitiveSet && (
                        <div className={classes.settingSection}>
                            <h4 className={classes.settingSectionTitle}>
                                Competitive Set
                                <span className={classes.keywordCount}>
                                    ({lockedRankNames.length + additionalRankSelected.length}/{MAX_RANK_COMPETITIVE_SET} selected)
                                </span>
                            </h4>
                            {lockedRankNames.length > 0 && (
                                <div className={classes.customKeywordsChips} style={{ marginBottom: "0.5rem" }}>
                                    {lockedRankNames.map((name) => (
                                        <Chip
                                            key={name}
                                            label={name}
                                            title="Selected on the form — always included"
                                            sx={{
                                                background: "#f1f5f9",
                                                border: "1px solid #cbd5e1",
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                            <MUIMultiSelect
                                loading={keyPlayersLoading}
                                disabled={viewMode}
                                fullWidth={true}
                                optionValue={""}
                                label=""
                                placeholder="Select additional competitors"
                                value={additionalRankSelected}
                                options={rankCompetitiveSetOptions}
                                allowSettingPrimary={false}
                                shouldShowValue={true}
                                allowCustomOption={!viewMode}
                                onChange={handleRankCompetitiveSetChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
